import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import { C } from "@/brand/tokens";
import { resourcesSection6, resourceCategories6 } from "@/brand/contentF6";
import { fetchResources } from "@/lib/resourcesApi";
import { Box6 } from "@/components/common/Box6";
import { Eyebrow6 } from "@/components/common/Eyebrow6";
import { Pill6 } from "@/components/common/Pill6";
import { TextLink6 } from "@/components/common/TextLink6";

const catLabel = Object.fromEntries(resourceCategories6.map((c) => [c.id, c.label]));

/** ResourcesPreviewSection6 — three category boxes + three featured/recent cards from the CMS. */
export function ResourcesPreviewSection6() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let active = true;
    fetchResources()
      .then((all) => {
        if (!active) return;
        const featured = all.filter((r) => r.featured);
        const rest = all.filter((r) => !r.featured);
        setItems([...featured, ...rest].slice(0, 3));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  return (
    <section id="resources" data-testid="resources-preview-6" style={{ background: C.surfaceLight2, color: C.textOnLight }}>
      <div className="mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-10 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-10 mb-12 items-end">
          <div className="lg:col-span-7">
            <Eyebrow6 className="mb-6">{resourcesSection6.eyebrow}</Eyebrow6>
            <h2
              className="font-cormorant"
              style={{
                color: C.textOnLight,
                fontSize: "clamp(1.9rem, 3.4vw, 2.8rem)",
                fontWeight: 400,
                lineHeight: 1.08,
                letterSpacing: "-0.012em",
                maxWidth: "26ch",
              }}
            >
              {resourcesSection6.headline}
            </h2>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 lg:pb-2">
            <TextLink6 to={resourcesSection6.cta.href} testId="resources-explore-cta">
              {resourcesSection6.cta.label}
            </TextLink6>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-10" data-testid="resource-categories-row">
          {resourceCategories6.map((c, i) => (
            <Box6
              as={Link}
              key={c.id}
              to={`/resources?category=${c.id}`}
              tone="white"
              radius="resource"
              className="p-6 transition-colors"
              data-testid={`resource-category-${i}`}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = C.surfacePrimary)}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = C.brassLine)}
            >
              <p className="font-cormorant mb-2" style={{ color: C.textOnLight, fontSize: "1.3rem", lineHeight: 1.15 }}>
                {c.label}
              </p>
              <p className="font-inter leading-[1.6]" style={{ color: C.inkOnLight, fontSize: "0.88rem" }}>
                {c.dek}
              </p>
            </Box6>
          ))}
        </div>

        <div className="grid md:grid-cols-3 gap-5" data-testid="resources-featured-grid">
          {items.map((r, i) => (
            <Box6
              as={Link}
              key={r.id}
              to={`/resources/${r.slug}`}
              tone="white"
              radius="resource"
              className="group flex flex-col p-7 lg:p-8 transition-colors"
              style={{ minHeight: 280 }}
              data-testid={`resource-card-${i}`}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = C.surfacePrimary)}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = C.brassLine)}
            >
              <div className="flex items-center justify-between gap-3 mb-5">
                <Pill6 tone="blue">{catLabel[r.category] || r.category}</Pill6>
                <span className="font-inter tabular-nums text-[10px] uppercase font-semibold shrink-0" style={{ color: C.inkOnLightSoft, letterSpacing: "0.18em" }}>
                  {r.publish_date}
                </span>
              </div>
              <h3 className="font-cormorant" style={{ color: C.textOnLight, fontSize: "clamp(1.15rem, 1.5vw, 1.35rem)", fontWeight: 400, lineHeight: 1.2 }}>
                {r.title}
              </h3>
              <p className="mt-4 font-inter leading-[1.65] grow" style={{ color: C.inkOnLight, fontSize: "0.9rem" }}>
                {r.summary}
              </p>
              <div className="mt-6 pt-4 flex items-center justify-between" style={{ borderTop: `1px solid ${C.brassSoft}` }}>
                <span className="font-inter text-[10px] uppercase font-semibold" style={{ color: C.accentFunctional, letterSpacing: "0.22em" }}>
                  Read
                </span>
                <ArrowRight size={12} weight="bold" className="transition-transform group-hover:translate-x-1" style={{ color: C.accentFunctional }} />
              </div>
            </Box6>
          ))}
        </div>
      </div>
    </section>
  );
}
